import React, { useState, useRef } from 'react';
import styles from './MobileFeedbackButton.module.css';

const MobileFeedbackButton = () => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feature');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [collectDiagnostics, setCollectDiagnostics] = useState(true);
  const modalRef = useRef(null);

  const handleFeedbackClick = () => {
    setIsFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
    setDescription('');
    setScreenshot(null);
  };

  const getDiagnosticInfo = () => {
    if (!collectDiagnostics) return null;

    return {
      browser: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      language: navigator.language
    };
  };

  const handleScreenshot = async () => {
    try {
      // Temporarily hide the modal
      if (modalRef.current) {
        modalRef.current.style.display = 'none';
      }

      // Wait a brief moment for the modal to hide
      await new Promise(resolve => setTimeout(resolve, 100));

      const stream = await navigator.mediaDevices.getDisplayMedia({ preferCurrentTab: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      setScreenshot(blob);

      stream.getTracks().forEach(track => track.stop());

      // Show the modal again
      if (modalRef.current) {
        modalRef.current.style.display = 'flex';
      }
    } catch (error) {
      console.error('Screenshot failed:', error);
      // Ensure modal is visible again if there's an error
      if (modalRef.current) {
        modalRef.current.style.display = 'flex';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('type', feedbackType);
    formData.append('description', description);

    if (screenshot) {
      formData.append('screenshot', screenshot);
    }

    if (collectDiagnostics) {
      formData.append('diagnostics', JSON.stringify(getDiagnosticInfo()));
    }

    try {
      // Use the API base URL from environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? 'https://api.lotwleague.com'
        : 'http://localhost:3001';

      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`Feedback submitted successfully! Issue: ${result.issueKey}`);
        handleCloseFeedback();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <>
      <button
        className={styles.feedbackButton}
        onClick={handleFeedbackClick}
        aria-label="Feedback"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className={styles.feedbackIcon}>
          <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z"/>
        </svg>
      </button>

      {isFeedbackOpen && (
        <div className={styles.feedbackModal} ref={modalRef}>
          <div className={styles.feedbackContent}>
            <div className={styles.modalHeader}>
              <h2>Submit Feedback</h2>
              <button
                className={styles.closeButton}
                onClick={handleCloseFeedback}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.feedbackForm}>
              <div className={styles.formGroup}>
                <label htmlFor="feedbackType">Type</label>
                <select
                  id="feedbackType"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <option value="feature">Feature Request</option>
                  <option value="bug">Report Bug</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  placeholder="Describe your feedback..."
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="screenshot">Screenshot</label>
                <button
                  id="screenshot"
                  type="button"
                  onClick={handleScreenshot}
                  className={styles.screenshotButton}
                >
                  Capture Screenshot
                </button>
                {screenshot && <span className={styles.screenshotStatus}>Screenshot captured!</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={collectDiagnostics}
                    onChange={(e) => setCollectDiagnostics(e.checked)}
                  />
                  Include system diagnostics
                </label>
              </div>

              <div className={styles.buttonGroup}>
                <button type="submit" className={styles.submitButton}>
                  Submit
                </button>
                <button
                  type="button"
                  onClick={handleCloseFeedback}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileFeedbackButton;
